import React from "react";
import { Card, H1, H3, Tag, Spinner, NonIdealState, Button } from "@blueprintjs/core";
import { inject, observer } from "mobx-react";
import { apiCallGet } from "../fns/util";
import cogoToast from "cogo-toast";
import { withRouter } from "react-router";
import { indianRupee, niceDateTime } from "../fns/util";

class ImplantBillPage extends React.Component {
  state = {
    isLoading: true,
    booking: null,
    implantBill: null,
    error: null,
  };

  componentDidMount() {
    this.loadImplantData();
  }

  loadImplantData = async () => {
    try {
      const sxId = this.props.match.params.sxId;
      if (!sxId) {
        this.setState({
          error: "Surgery ID not provided",
          isLoading: false,
        });
        return;
      }

      console.log("🔄 Loading implant data for surgery:", sxId);
      
      // Load booking data with implant information
      const response = await apiCallGet(
        `/v3/${this.props.authStore.prefix}/neoSxBookingMaster/getByIdForBill/${sxId}`
      );

      console.log("📄 Implant data response:", response);

      if (response && response.success && response.data) {
        // Extract implant items from consumption items
        const consumptionItems = response.data.consumptionItems || [];
        const implantItems = consumptionItems.filter(item => 
          item.itemType === "implant" || 
          item.category === "IOL" || 
          item.itemName?.toLowerCase().includes("iol") ||
          item.itemName?.toLowerCase().includes("implant") ||
          item.itemName?.toLowerCase().includes("lens")
        );

        this.setState({
          booking: response.data,
          implantBill: {
            items: implantItems,
            totalAmount: implantItems.reduce((sum, item) => sum + (item.totalPaise || item.totalAmount * 100 || 0), 0)
          },
          isLoading: false,
        });
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err) {
      console.error("❌ Error loading implant data:", err);
      this.setState({
        error: "Failed to load implant data",
        isLoading: false,
      });
      cogoToast.error("Failed to load implant data");
    }
  };

  renderImplantItem = (item, index) => {
    return (
      <Card key={index} style={{ marginBottom: "10px", backgroundColor: "var(--bg-surface)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            <H3 style={{ color: "var(--text-primary)", margin: 0, marginBottom: "10px" }}>
              {item.itemName || `Implant Item #${index + 1}`}
            </H3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
              {item.serialNo && (
                <Tag large minimal>
                  Serial: {item.serialNo}
                </Tag>
              )}
              {(item.model || item.power) && (
                <Tag large minimal>
                  {item.model ? `Model: ${item.model}` : `Power: ${item.power}`}
                </Tag>
              )}
              <Tag large minimal>
                Qty: {item.qty || item.quantity || 1}
              </Tag>
              <Tag large minimal intent="primary">
                Rate: {indianRupee(item.pricePaise || item.rate * 100 || 0)}
              </Tag>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <Tag large minimal intent="success" style={{ fontSize: "16px" }}>
              Total: {indianRupee(item.totalPaise || item.totalAmount * 100 || 0)}
            </Tag>
          </div>
        </div>
      </Card>
    );
  };

  renderImplantSummary = () => {
    const { implantBill } = this.state;
    
    return (
      <Card style={{ marginBottom: "20px", backgroundColor: "var(--bg-surface)" }}>
        <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Implant Bill Summary</H3>
        <div style={{ marginTop: "15px", display: "flex", gap: "20px" }}>
          <div>
            <Tag large minimal>
              Total Items: {implantBill.items?.length || 0}
            </Tag>
          </div>
          <div>
            <Tag large minimal intent="success">
              Total Amount: {indianRupee(implantBill.totalAmount || 0)}
            </Tag>
          </div>
        </div>
      </Card>
    );
  };

  renderPatientInfo = () => {
    const { booking } = this.state;
    
    return (
      <Card style={{ marginBottom: "20px", backgroundColor: "var(--bg-surface)" }}>
        <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Patient Information</H3>
        <div style={{ marginTop: "15px" }}>
          <div style={{ marginBottom: "10px" }}>
            <Tag large minimal>Patient Name: {booking.patientName || "Unknown"}</Tag>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <Tag large minimal>Patient ID: {booking.patientId || "Unknown"}</Tag>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <Tag large minimal>Booking Reference: {booking.bookingReference || "Unknown"}</Tag>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <Tag large minimal>Doctor: {booking.doctorName || "Unknown"}</Tag>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <Tag large minimal>Branch: {booking.branchName || "Unknown"}</Tag>
          </div>
          <div>
            <Tag large minimal>Surgery Date: {booking.scheduledDate ? new Date(booking.scheduledDate).toLocaleDateString() : "Unknown"}</Tag>
          </div>
        </div>
      </Card>
    );
  };

  render() {
    if (this.state.isLoading) {
      return (
        <div style={{ 
          padding: "20px", 
          backgroundColor: "var(--bg-primary)", 
          color: "var(--text-primary)", 
          minHeight: "100vh" 
        }}>
          <NonIdealState
            icon={<Spinner />}
            title="Loading Implant Bill Data..."
            description="Please wait while we fetch the implant information."
          />
        </div>
      );
    }

    if (this.state.error) {
      return (
        <div style={{ 
          padding: "20px", 
          backgroundColor: "var(--bg-primary)", 
          color: "var(--text-primary)", 
          minHeight: "100vh" 
        }}>
          <NonIdealState
            icon="error"
            title="Error Loading Implant Data"
            description={this.state.error}
            action={
              <Button 
                intent="primary" 
                icon="refresh" 
                onClick={this.loadImplantData}
              >
                Retry
              </Button>
            }
          />
        </div>
      );
    }

    const { booking, implantBill } = this.state;
    const implantItems = implantBill?.items || [];

    return (
      <div style={{ 
        padding: "20px", 
        backgroundColor: "var(--bg-primary)", 
        color: "var(--text-primary)", 
        minHeight: "100vh" 
      }}>
        <div style={{ marginBottom: "20px" }}>
          <H1 style={{ color: "var(--text-primary)", margin: 0 }}>
            Implant/IOL Bill
          </H1>
          <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
            Surgery ID: {this.props.match.params.sxId}
          </p>
        </div>

        {/* Patient Information */}
        {this.renderPatientInfo()}

        {/* Implant Bill Summary */}
        {this.renderImplantSummary()}

        {/* Implant Items */}
        <div>
          <H3 style={{ color: "var(--text-primary)", marginBottom: "15px" }}>
            Implant Items ({implantItems.length} items)
          </H3>
          
          {implantItems.length === 0 ? (
            <NonIdealState
              icon="eye-open"
              title="No Implant Items Found"
              description="No implant or IOL items have been recorded for this surgery."
            />
          ) : (
            <div>
              {implantItems.map((item, index) => this.renderImplantItem(item, index))}
            </div>
          )}
        </div>

        {/* Print Actions */}
        <div style={{ marginTop: "30px", textAlign: "center" }}>
          <Button 
            intent="primary" 
            icon="print" 
            large
            onClick={() => window.print()}
            style={{ marginRight: "10px" }}
          >
            Print Bill
          </Button>
          <Button 
            icon="arrow-left" 
            large
            onClick={() => window.history.back()}
          >
            Back to Billing
          </Button>
        </div>
      </div>
    );
  }
}

export default inject("authStore")(observer(withRouter(ImplantBillPage)));
